import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { reportApi } from '../api/report'

// Editorial palette — kept inline so the modal can't drift from the
// practitioner report it generates (app/api/report.py renders the
// same gold/ink/cream story).
const INK = '#1C1814'
const CREAM = '#F2EDE3'
const GOLD = '#9E8456'
const SOFT_WHITE = '#FDFAF6'
const MUTED = '#8A8580'
const CARD_BG = '#F7F3EE'
const DIVIDER = '#E8E2D9'
const INK_HOVER = '#2C2820'
const SAGE = '#3D5C3A'

const STYLES = `
@keyframes srm-fade-in {
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
}
@keyframes srm-backdrop-fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.srm-backdrop { animation: srm-backdrop-fade 200ms ease-out forwards; }
.srm-shell    { animation: srm-fade-in 200ms ease-out forwards; }

/* Hide scrollbar but keep scrolling — Safari/Chrome/Firefox/IE */
.srm-shell::-webkit-scrollbar { display: none; }
.srm-shell { scrollbar-width: none; -ms-overflow-style: none; }

.srm-close { transition: color 150ms ease-out; }
.srm-close:hover { color: ${INK} !important; }

.srm-btn-primary { transition: background-color 200ms ease-out, color 200ms ease-out; }
.srm-btn-primary:not(:disabled):hover { background: ${INK_HOVER} !important; }

.srm-btn-secondary { transition: background-color 150ms ease-out, color 150ms ease-out; }
.srm-btn-secondary:not(:disabled):hover { background: ${CREAM} !important; }

.srm-copy-link { transition: opacity 150ms ease-out; }
.srm-copy-link:hover { opacity: 0.7; }

.srm-action-chip { transition: background-color 150ms ease-out; }
.srm-action-chip:hover { background: ${CREAM} !important; }

.srm-revoke { transition: color 150ms ease-out; }
.srm-revoke:hover { color: #9E5A4A !important; }
`

function TrendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={GOLD} strokeWidth="1.6" aria-hidden="true">
      <path d="M3 14l4-5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 5h4v4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PatternIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={GOLD} strokeWidth="1.6" aria-hidden="true">
      <circle cx="9" cy="9" r="5" />
      <path d="M13 13l3.5 3.5" strokeLinecap="round" />
    </svg>
  )
}

function NoteIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={GOLD} strokeWidth="1.6" aria-hidden="true">
      <path d="M5 3h7l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" strokeLinejoin="round" />
      <path d="M7 9h6M7 12h6M7 15h4" strokeLinecap="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 9.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M7.5 10.5l3-3M6.75 8.25l-1.5 1.5a2.25 2.25 0 003.182 3.182l1.5-1.5M11.25 9.75l1.5-1.5a2.25 2.25 0 00-3.182-3.182l-1.5 1.5" strokeLinecap="round" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M9 3v9m0 0l-3-3m3 3l3-3M3 13.5v1.5h12v-1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const eyebrowStyle = {
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: GOLD,
  fontWeight: 600,
}

const cardEyebrowStyle = {
  ...eyebrowStyle,
  marginBottom: 8,
}

// Small ink-on-cream toggle, local to the modal so the design-token
// surface stays untouched.
function ConsentToggle({ value, onChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value ? 'true' : 'false'}
      aria-label="Include journal excerpts"
      onClick={() => { if (!disabled) onChange(!value) }}
      disabled={disabled}
      style={{
        width: 42, height: 24, borderRadius: 99, flexShrink: 0,
        background: value ? GOLD : DIVIDER,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        position: 'relative',
        transition: 'background 0.2s',
        padding: 0,
      }}
    >
      <span style={{
        width: 18, height: 18, borderRadius: '50%', background: SOFT_WHITE,
        position: 'absolute', top: 3,
        left: value ? 21 : 3,
        transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }} />
    </button>
  )
}

// PDF generation runs 3–10s server-side (WeasyPrint). Staged copy
// keeps the wait honest instead of a bare spinner.
const PDF_STAGES = [
  'Preparing your report…',
  'Rendering charts and pages…',
  'Almost there — this can take up to 10 seconds.',
]

function fmtDay(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

export function ShareReportModal({ open, onClose }) {
  const { user, token } = useAuth()
  const [loading, setLoading] = useState(null) // 'share' | 'pdf' | null
  const [link, setLink] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  // Per-share consent. Defaults to the user-level research_consent and
  // can only NARROW it: when research_consent isn't true the toggle
  // never renders and the backend excludes excerpts regardless.
  const journalAllowed = user?.research_consent === true
  const [includeJournal, setIncludeJournal] = useState(journalAllowed)
  // Active (unexpired, unrevoked) links — fetched on open so the user
  // sees what's still out there and can cut any of them off.
  const [shares, setShares] = useState([])
  const [revokingId, setRevokingId] = useState(null)
  const [pdfStage, setPdfStage] = useState(0)

  // Reset + hydrate on every open.
  useEffect(() => {
    if (!open || !user) return
    setError(null)
    setLink(null)
    setCopied(false)
    setIncludeJournal(user?.research_consent === true)
    let cancelled = false
    reportApi.listShares(user.id, token)
      .then((data) => { if (!cancelled) setShares(data?.shares || []) })
      .catch(() => { /* list is additive — modal works without it */ })
    return () => { cancelled = true }
  }, [open, user, token])

  // Advance the PDF progress copy while the download is in flight.
  useEffect(() => {
    if (loading !== 'pdf') { setPdfStage(0); return undefined }
    const t1 = setTimeout(() => setPdfStage(1), 2500)
    const t2 = setTimeout(() => setPdfStage(2), 5500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [loading])

  if (!open) return null

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch {
      setError("Couldn't copy automatically — long-press the link to copy.")
    }
  }

  async function refreshShares() {
    try {
      const data = await reportApi.listShares(user.id, token)
      setShares(data?.shares || [])
    } catch { /* non-blocking */ }
  }

  async function handleGenerateLink() {
    setLoading('share')
    setError(null)
    try {
      // Narrow-only: journalAllowed false forces excerpts off no matter
      // what the local toggle state claims.
      const data = await reportApi.share(user.id, token, 30, journalAllowed && includeJournal)
      const fullUrl = `${window.location.origin}/report/view/${data.token}`
      setLink(fullUrl)
      await copyToClipboard(fullUrl)
      refreshShares()
    } catch {
      setError('Failed to create share link. Try again.')
    } finally {
      setLoading(null)
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: 'My Haiven signal report',
        text: 'A 30-day summary of my signal — private link, expires in 7 days.',
        url: link,
      })
    } catch {
      // User dismissed the sheet — not an error.
    }
  }

  function mailtoHref() {
    const subject = encodeURIComponent('My Haiven signal report')
    const body = encodeURIComponent(
      `Here's a private link to my last 30 days of signal data:\n\n${link}\n\nThe link expires in 7 days.`
    )
    return `mailto:?subject=${subject}&body=${body}`
  }

  async function handleRevoke(shareId) {
    setRevokingId(shareId)
    setError(null)
    try {
      await reportApi.revokeShare(user.id, shareId, token)
      setShares((prev) => prev.filter((s) => s.id !== shareId))
      // If the link we just minted is the one revoked, clear it.
    } catch {
      setError("Couldn't revoke that link. Try again.")
    } finally {
      setRevokingId(null)
    }
  }

  async function handleDownloadPdf() {
    setLoading('pdf')
    setError(null)
    try {
      const blob = await reportApi.getPdf(user.id, token)
      // Defensive: with the WeasyPrint silent-fallback path retired,
      // the backend always returns ``application/pdf`` on a 2xx. If
      // we ever see a non-PDF blob slip through, surface that instead
      // of saving a misleading .pdf the user can't open.
      if (blob.type && blob.type !== 'application/pdf') {
        throw new Error('Server returned a non-PDF response. Please try again.')
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `haiven-report-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.message || 'PDF download failed. Try again.')
    } finally {
      setLoading(null)
    }
  }

  const bullets = [
    { Icon: TrendIcon, text: '30-day Signal Score trend and Body/Mind/Behavior breakdown' },
    { Icon: PatternIcon, text: 'Your current pattern and where it’s trending' },
    { Icon: NoteIcon, text: 'A plain-language summary of your patterns — in your words, not an algorithm’s' },
  ]

  const linkActionStyle = {
    flex: 1,
    height: 40,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: 'transparent',
    color: INK,
    border: `1px solid ${DIVIDER}`,
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textDecoration: 'none',
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
    >
      <style>{STYLES}</style>
      <div
        className="srm-backdrop absolute inset-0"
        style={{ background: 'rgba(28, 24, 20, 0.45)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      />

      <div
        className="srm-shell relative w-full"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 512, // max-w-lg
          // Cap to the safe viewport so a tall, centered modal never pushes
          // its top edge under the Dynamic Island / status bar. Mirrors the
          // env(safe-area-inset) pattern used for screen tops.
          maxHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px)',
          overflowY: 'auto',
          background: SOFT_WHITE,
          borderRadius: 24,
          boxShadow: '0 24px 60px rgba(28, 24, 20, 0.18), 0 4px 16px rgba(28, 24, 20, 0.08)',
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="srm-close"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: MUTED,
            cursor: 'pointer',
            borderRadius: 8,
            padding: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>

        <div
          style={{
            padding: 28,
          }}
          className="srm-pad"
        >
          {/* Desktop padding override via media query inside style block below.
              Inline style on .srm-pad sets the mobile baseline (28px). */}
          <style>{`
            @media (min-width: 640px) {
              .srm-pad { padding: 40px !important; }
            }
          `}</style>

          {/* ── Header ──────────────────────────────────────────────── */}
          <header style={{ marginBottom: 28 }}>
            <h2
              id="share-modal-title"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 26,
                fontWeight: 600,
                color: INK,
                lineHeight: 1.18,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Share Your Report
            </h2>
            <p
              style={{
                marginTop: 6,
                fontSize: 15,
                color: MUTED,
                lineHeight: 1.6,
                maxWidth: 340,
                fontWeight: 400,
              }}
            >
              Share a window into your last 30 days with anyone you choose — without explaining everything from scratch.
            </p>
          </header>

          {/* ── What's in the report ────────────────────────────────── */}
          <section style={{ marginBottom: 28 }}>
            <div style={{ ...eyebrowStyle, marginBottom: 16 }}>
              What's in the report
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {bullets.map(({ Icon, text }, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    minHeight: 48,
                    padding: '4px 0',
                    marginBottom: i === bullets.length - 1 ? 0 : 12,
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: CREAM,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon />
                  </span>
                  <span style={{ fontSize: 15, color: INK, lineHeight: 1.5, fontWeight: 400 }}>
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── Per-share consent ───────────────────────────────────── */}
          <section
            style={{
              background: CARD_BG,
              borderRadius: 16,
              padding: '16px 20px',
              marginBottom: 20,
            }}
          >
            {journalAllowed ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 14, color: INK, fontWeight: 500, marginBottom: 3 }}>
                    Include journal excerpts
                  </div>
                  <p style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55, margin: 0 }}>
                    A few short reflections from this window. You decide for each link — this one only.
                  </p>
                </div>
                <ConsentToggle
                  value={includeJournal}
                  onChange={setIncludeJournal}
                  disabled={loading !== null}
                />
              </div>
            ) : (
              <p style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.6, margin: 0 }}>
                Journal excerpts are never included in your shared report.
              </p>
            )}
          </section>

          {/* ── Divider ─────────────────────────────────────────────── */}
          <div
            aria-hidden="true"
            style={{ height: 1, background: DIVIDER, margin: '20px 0 28px' }}
          />

          {/* ── Error banner ────────────────────────────────────────── */}
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: 12,
                fontSize: 13,
                borderRadius: 12,
                background: 'rgba(158, 90, 74, 0.08)',
                color: '#9E5A4A',
                border: '1px solid rgba(158, 90, 74, 0.18)',
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}

          {/* ── Option 1: Send a Link ───────────────────────────────── */}
          <section
            style={{
              background: CARD_BG,
              borderRadius: 20,
              padding: 24,
              marginBottom: 16,
            }}
          >
            <div style={cardEyebrowStyle}>Send a Link</div>
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 20 }}>
              Generates a private link you can text or email. Expires in 7 days. Shows your first name only.
            </p>

            <button
              onClick={handleGenerateLink}
              disabled={loading !== null}
              aria-live="polite"
              className="srm-btn-primary"
              style={{
                width: '100%',
                height: 52,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: copied ? SAGE : INK,
                color: copied ? '#FFFFFF' : CREAM,
                border: 'none',
                borderRadius: 14,
                fontSize: 15,
                fontWeight: 500,
                letterSpacing: '0.02em',
                fontFamily: 'inherit',
                cursor: loading !== null ? 'not-allowed' : 'pointer',
                opacity: loading !== null ? 0.75 : 1,
              }}
            >
              {loading === 'share' ? (
                <span
                  className="rounded-full animate-spin"
                  style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%' }}
                />
              ) : copied ? (
                <>
                  <CheckIcon />
                  Link Copied
                </>
              ) : (
                <>
                  <LinkIcon />
                  {link ? 'Generate New Link' : 'Generate Link'}
                </>
              )}
            </button>

            {link && (
              <div style={{ marginTop: 14 }} aria-live="polite">
                <p
                  style={{
                    fontSize: 12,
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                    color: MUTED,
                    lineHeight: 1.5,
                    wordBreak: 'break-all',
                    margin: 0,
                  }}
                >
                  {link}
                </p>
                {/* Share mechanics — native sheet on mobile, copy + email
                    everywhere. mailto is the lowest-common-denominator
                    fallback for desktop without Web Share. */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {canNativeShare && (
                    <button
                      onClick={handleNativeShare}
                      className="srm-action-chip"
                      style={linkActionStyle}
                    >
                      Share…
                    </button>
                  )}
                  <button
                    onClick={() => copyToClipboard(link)}
                    className="srm-action-chip"
                    style={linkActionStyle}
                  >
                    Copy link
                  </button>
                  <a
                    href={mailtoHref()}
                    className="srm-action-chip"
                    style={linkActionStyle}
                  >
                    Email
                  </a>
                </div>
              </div>
            )}
          </section>

          {/* ── Option 2: Download PDF ──────────────────────────────── */}
          <section
            style={{
              background: CARD_BG,
              borderRadius: 20,
              padding: 24,
            }}
          >
            <div style={cardEyebrowStyle}>Download PDF</div>
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 20 }}>
              Save or print a full report to keep or share.
            </p>
            <button
              onClick={handleDownloadPdf}
              disabled={loading !== null}
              className="srm-btn-secondary"
              style={{
                width: '100%',
                height: 52,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: 'transparent',
                color: GOLD,
                border: `1.5px solid ${GOLD}`,
                borderRadius: 14,
                fontSize: 15,
                fontWeight: 500,
                letterSpacing: '0.02em',
                fontFamily: 'inherit',
                cursor: loading !== null ? 'not-allowed' : 'pointer',
                opacity: loading !== null ? 0.75 : 1,
              }}
            >
              {loading === 'pdf' ? (
                <span
                  className="rounded-full animate-spin"
                  style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%' }}
                />
              ) : (
                <>
                  <DownloadIcon />
                  Download PDF
                </>
              )}
            </button>
            {loading === 'pdf' && (
              <p
                aria-live="polite"
                style={{ marginTop: 10, marginBottom: 0, fontSize: 12.5, color: MUTED, textAlign: 'center', lineHeight: 1.5 }}
              >
                {PDF_STAGES[pdfStage]}
              </p>
            )}
          </section>

          {/* ── Active links ────────────────────────────────────────── */}
          {shares.length > 0 && (
            <section style={{ marginTop: 24 }}>
              <div style={{ ...eyebrowStyle, marginBottom: 12 }}>
                Active links
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {shares.map((s) => (
                  <li
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 0',
                      borderBottom: `1px solid ${DIVIDER}`,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: INK, fontWeight: 400 }}>
                        Created {fmtDay(s.created_at)} · expires {fmtDay(s.expires_at)}
                      </div>
                      <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
                        Journal excerpts {s.include_journal ? 'included' : 'off'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(s.id)}
                      disabled={revokingId !== null}
                      className="srm-revoke"
                      aria-label={`Revoke link created ${fmtDay(s.created_at)}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '6px 4px',
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: MUTED,
                        cursor: revokingId !== null ? 'default' : 'pointer',
                        fontFamily: 'inherit',
                        flexShrink: 0,
                      }}
                    >
                      {revokingId === s.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 8, marginBottom: 0, fontSize: 11.5, color: MUTED, lineHeight: 1.6 }}>
                Revoking a link cuts off access immediately, even before it expires.
              </p>
            </section>
          )}

          {/* ── Footer ──────────────────────────────────────────────── */}
          <p
            style={{
              marginTop: 24,
              marginBottom: 0,
              fontSize: 12,
              color: MUTED,
              lineHeight: 1.7,
              textAlign: 'center',
              fontWeight: 400,
            }}
          >
            Your data is never shared without your action.<br />
            Links expire automatically — and you can revoke them anytime.
          </p>
        </div>
      </div>
    </div>
  )
}
